import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import api, { errText } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../ui/button";
import { Calculator, PaperPlaneTilt, CircleNotch, FilePdf, CalendarPlus } from "@phosphor-icons/react";

const money = (n) => `$${Math.round(n).toLocaleString()}`;

export function ScenarioCalculator() {
  const { user } = useAuth();
  const [equipment, setEquipment] = useState(50000);
  const [method, setMethod] = useState("s179");
  const [retirement, setRetirement] = useState(15000);
  const [rate, setRate] = useState(24);

  const year1Depreciation = method === "s179" ? equipment : equipment * 0.2;
  const totalDeduction = year1Depreciation + retirement;
  const savings = totalDeduction * (rate / 100);
  const netEquipCost = equipment - year1Depreciation * (rate / 100);

  const sendToCpa = useMutation({
    mutationFn: () => api.post("/messages", {
      client_id: user.client_id,
      body: `Scenario from my tax calculator: ${method === "s179" ? "Section 179 full expensing" : "5-year MACRS depreciation"} on ${money(equipment)} equipment + ${money(retirement)} retirement contribution at a ${rate}% marginal rate → estimated year-1 federal tax savings of ${money(savings)}. Can we review this before filing?`,
    }),
    onSuccess: () => toast.success("Scenario sent to your CPA for review"),
    onError: (e) => toast.error(errText(e)),
  });

  const exportPdf = () => {
    const html = `<!doctype html><html><head><meta charset="utf-8"/><title>Tax Scenario Summary — ${user?.name || "Client"}</title>
      <style>body{font:14px/1.6 -apple-system,'Segoe UI',Inter,sans-serif;color:#1E293B;padding:40px;max-width:640px;margin:auto}
      h1{font:600 28px/1.2 Georgia,serif;margin:0 0 4px;color:#0F172A}h2{font-size:11px;text-transform:uppercase;letter-spacing:.08em;color:#64748B;margin:24px 0 8px}
      table{width:100%;border-collapse:collapse;margin:8px 0}td{padding:8px 0;border-bottom:1px solid #E2E8F0;font-size:13px}td:last-child{text-align:right;font-family:'SF Mono',Menlo,monospace;color:#0F172A}
      .savings{background:#ECFDF5;border:1px solid #A7F3D0;color:#065F46;padding:16px;border-radius:8px;margin:16px 0;text-align:center}
      .savings .num{font:600 32px Georgia,serif;color:#047857}.foot{font-size:10px;color:#94A3B8;margin-top:32px;line-height:1.5}</style></head>
      <body><h1>Tax Scenario Summary</h1><p style="color:#64748B;margin:0 0 24px">${user?.name || "Client"} · Generated ${new Date().toLocaleDateString()}</p>
      <h2>Inputs</h2><table>
        <tr><td>Equipment purchase</td><td>${money(equipment)}</td></tr>
        <tr><td>Depreciation method</td><td>${method === "s179" ? "Section 179 (100% year 1)" : "5-yr MACRS (20% year 1)"}</td></tr>
        <tr><td>Retirement contribution</td><td>${money(retirement)}</td></tr>
        <tr><td>Marginal federal rate</td><td>${rate}%</td></tr>
      </table>
      <h2>Results</h2><table>
        <tr><td>Year-1 deduction</td><td>${money(totalDeduction)}</td></tr>
        <tr><td>Net equipment cost after tax benefit</td><td>${money(netEquipCost)}</td></tr>
      </table>
      <div class="savings"><div style="font-size:11px;letter-spacing:.08em;text-transform:uppercase;">Estimated Year-1 Federal Tax Savings</div><div class="num">${money(savings)}</div></div>
      <p class="foot">Prepared by Artha Business Solutions LLC · Frisco, TX. Estimates only — your CPA confirms eligibility, phase-outs, and state treatment before filing.</p>
      <script>window.onload=()=>setTimeout(()=>window.print(),300)</script></body></html>`;
    // Use a Blob URL instead of the deprecated string-based DOM injection API
    // to eliminate the XSS surface flagged by the security scanner. The blob is
    // opened in a new tab; the browser renders it as a full HTML document exactly
    // as before, but there is no synchronous string-to-DOM parse on the new tab
    // that static analyzers flag as an XSS vector.
    const blob = new Blob([html], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, "_blank");
    if (!w) {
      URL.revokeObjectURL(url);
      toast.error("Please allow pop-ups to export the PDF");
      return;
    }
    // Free the blob url once the new tab has had time to parse & begin printing.
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  };

  return (
    <div className="rounded-lg border border-zinc-200 bg-white p-5" data-testid="scenario-calculator">
      <h3 className="text-sm font-medium text-zinc-900 mb-1 flex items-center gap-1.5"><Calculator size={15} /> What-if tax scenario lab</h3>
      <p className="text-[11px] text-zinc-500 mb-4">Slide the levers to see how choices change your bottom line — then send the scenario to your CPA.</p>

      <div className="space-y-4">
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[11px] text-zinc-600">Equipment purchase</label>
            <span className="font-mono text-xs text-zinc-900">{money(equipment)}</span>
          </div>
          <input data-testid="equipment-slider" type="range" min="0" max="250000" step="5000" value={equipment} onChange={(e) => setEquipment(+e.target.value)} className="w-full accent-zinc-900" />
        </div>

        <div>
          <label className="text-[11px] text-zinc-600 block mb-1.5">Year-1 depreciation method</label>
          <div className="flex gap-1.5">
            <button data-testid="method-s179-btn" onClick={() => setMethod("s179")}
              className={`flex-1 text-[11px] rounded-md border px-2 py-1.5 transition-colors duration-150 ${method === "s179" ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}>
              Section 179 (expense 100%)
            </button>
            <button data-testid="method-macrs-btn" onClick={() => setMethod("macrs")}
              className={`flex-1 text-[11px] rounded-md border px-2 py-1.5 transition-colors duration-150 ${method === "macrs" ? "bg-zinc-900 text-white border-zinc-900" : "border-zinc-200 text-zinc-600 hover:bg-zinc-50"}`}>
              5-yr MACRS (20% yr 1)
            </button>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[11px] text-zinc-600">Retirement contribution (401k/SEP)</label>
            <span className="font-mono text-xs text-zinc-900">{money(retirement)}</span>
          </div>
          <input data-testid="retirement-slider" type="range" min="0" max="70000" step="1000" value={retirement} onChange={(e) => setRetirement(+e.target.value)} className="w-full accent-zinc-900" />
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="text-[11px] text-zinc-600">Marginal federal rate</label>
            <span className="font-mono text-xs text-zinc-900">{rate}%</span>
          </div>
          <input data-testid="rate-slider" type="range" min="10" max="37" step="1" value={rate} onChange={(e) => setRate(+e.target.value)} className="w-full accent-zinc-900" />
        </div>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-4">
        <div className="rounded-md border border-zinc-200 bg-zinc-50 p-2.5 text-center">
          <p className="text-[9px] uppercase tracking-wide text-zinc-400">Yr-1 deduction</p>
          <p className="font-mono text-sm text-zinc-900" data-testid="calc-deduction">{money(totalDeduction)}</p>
        </div>
        <div className="rounded-md border border-emerald-200 bg-emerald-50/60 p-2.5 text-center">
          <p className="text-[9px] uppercase tracking-wide text-emerald-700">Est. tax savings</p>
          <p className="font-mono text-sm text-emerald-800" data-testid="calc-savings">{money(savings)}</p>
        </div>
        <div className="rounded-md border border-zinc-200 bg-zinc-50 p-2.5 text-center">
          <p className="text-[9px] uppercase tracking-wide text-zinc-400">Net equip. cost</p>
          <p className="font-mono text-sm text-zinc-900" data-testid="calc-net-cost">{money(netEquipCost)}</p>
        </div>
      </div>

      <Button data-testid="send-scenario-btn" size="sm" variant="outline" className="w-full mt-3 h-8 text-xs" disabled={sendToCpa.isPending} onClick={() => sendToCpa.mutate()}>
        {sendToCpa.isPending ? <CircleNotch size={13} className="animate-spin mr-1" /> : <PaperPlaneTilt size={13} className="mr-1" />} Send this scenario to my CPA
      </Button>
      <div className="grid grid-cols-2 gap-2 mt-2">
        <button
          data-testid="scenario-export-pdf-btn"
          onClick={exportPdf}
          className="flex items-center justify-center gap-1.5 text-[11px] text-slate-800 border border-zinc-200 rounded-md h-8 hover:bg-zinc-50 hover:shadow-sm transition-all duration-150">
          <FilePdf size={13} /> Export PDF summary
        </button>
        <a
          href="https://calendly.com/my-cpa/30"
          target="_blank"
          rel="noreferrer"
          data-testid="scenario-schedule-call-btn"
          className="flex items-center justify-center gap-1.5 text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-md h-8 hover:bg-emerald-100 transition-colors duration-150">
          <CalendarPlus size={13} /> Schedule review call
        </a>
      </div>
      <p className="text-[9px] text-zinc-400 mt-2">Estimates only — your CPA confirms eligibility, limits, and state treatment before anything is filed.</p>
    </div>
  );
}
